import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@atoms/Input';
import { Spinner } from '@atoms/Spinner';
import { Button } from '@atoms/Button';
import { ProductCard } from '@molecules/ProductCard';
import { WholesalerCard } from '@molecules/WholesalerCard';
import { ProductBrowserModal } from '@molecules/ProductBrowserModal';
import { LocationMapModal } from '@molecules/ServiceAreaMap';
import { catalogService } from '@services/catalogService';
import { geoService } from '@services/geoService';
import { searchService } from '@services/searchService';
import { useAuthStore } from '@store/authStore';
import { useAddToCart } from '@/hooks/useAddToCart';
import { formatCurrency } from '@utils/formatters';
import type { Product, User } from '@/types';

export function BrowseSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [browserWholesaler, setBrowserWholesaler] = useState<User | null>(null);
  const [showLocationMap, setShowLocationMap] = useState(false);
  const wholesalerId =
    searchParams.get('wholesaler') ??
    searchParams.get('open_wholesaler') ??
    undefined;
  const category = searchParams.get('category') ?? undefined;
  const [searchQuery, setSearchQuery] = useState('');
  const { requestAdd } = useAddToCart('/restaurant?tab=browse');
  const { user } = useAuthStore();

  const needsLocation = !user?.city && !user?.latitude;

  const { data: products, isLoading } = useQuery({
    queryKey: ['restaurant', 'products', wholesalerId, category],
    queryFn: () => catalogService.getProducts({ wholesalerId, category }),
    enabled: searchQuery.trim().length < 2,
  });

  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['restaurant', 'search', searchQuery, wholesalerId],
    queryFn: () => searchService.searchProducts(searchQuery.trim(), wholesalerId),
    enabled: searchQuery.trim().length >= 2,
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => catalogService.getCategories(),
  });

  const { data: geoValidation } = useQuery({
    queryKey: ['geo', 'validate', wholesalerId],
    queryFn: () => geoService.validateWholesaler(wholesalerId!),
    enabled: Boolean(wholesalerId),
  });

  const handleAdd = (product: Product) => {
    requestAdd(product);
  };

  const displayProducts = searchQuery.trim().length >= 2 ? searchResults : products;
  const loading = searchQuery.trim().length >= 2 ? searchLoading : isLoading;

  if (loading) return <Spinner />;

  const wholesalers = [...new Map(
    (displayProducts ?? [])
      .filter((p) => p.wholesaler)
      .map((p) => [p.wholesaler!.id, p.wholesaler!]),
  ).values()];

  const updateParams = (updates: Record<string, string | undefined>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'browse');
      Object.entries(updates).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {needsLocation && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Teslimat için konum bilginizi güncellemeniz önerilir.{' '}
          <Button size="sm" variant="secondary" className="ml-2" onClick={() => setShowLocationMap(true)}>
            Haritada Seç
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="ml-2"
            onClick={() => setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', 'settings');
              return next;
            })}
          >
            Ayarlara Git
          </Button>
        </div>
      )}

      <Input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Ürün ara (min. 2 karakter)..."
      />

      {wholesalerId && (
        <div
          className={`rounded-xl border p-4 text-sm ${
            !geoValidation
              ? 'border-red-200 bg-red-50 text-red-700'
              : geoValidation.outOfSchedule
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-green-200 bg-green-50 text-green-800'
          }`}
        >
          {!geoValidation && 'Bu toptancı hizmet bölgenizde değil. Sipariş veremezsiniz.'}
          {geoValidation?.outOfSchedule && 'Toptancı şu an teslimat saatleri dışında.'}
          {geoValidation && !geoValidation.outOfSchedule && (
            <>
              Teslimat ücreti: {formatCurrency(geoValidation.deliveryFee)} ·
              Min. sipariş: {formatCurrency(geoValidation.minOrderTotal)}
            </>
          )}
        </div>
      )}

      {!searchQuery.trim() && (
        <div>
          <h2 className="text-lg font-semibold">Toptancılar</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {wholesalers.map((w) => (
              <WholesalerCard
                key={w.id}
                wholesaler={w}
                onClick={() => setBrowserWholesaler(w)}
              />
            ))}
          </div>
        </div>
      )}

      {!searchQuery.trim() && categories && categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => updateParams({ category: undefined, wholesaler: wholesalerId })}
            className={`rounded-full px-3 py-1 text-sm ${!category ? 'bg-brand-500 text-white' : 'bg-gray-100'}`}
          >
            Tümü
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => updateParams({ category: cat.name, wholesaler: wholesalerId })}
              className={`rounded-full px-3 py-1 text-sm ${
                category === cat.name ? 'bg-brand-500 text-white' : 'bg-gray-100'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold">
          {searchQuery.trim().length >= 2 ? 'Arama Sonuçları' : 'Ürünler'}
        </h2>
        <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {(displayProducts ?? []).map((product) => (
            <ProductCard key={product.id} product={product} onAdd={handleAdd} showWholesaler={!wholesalerId} />
          ))}
        </div>
        {!displayProducts?.length && (
          <p className="py-8 text-center text-gray-500">Ürün bulunamadı</p>
        )}
      </div>

      <ProductBrowserModal
        wholesaler={browserWholesaler}
        onClose={() => setBrowserWholesaler(null)}
      />
      <LocationMapModal
        open={showLocationMap}
        onClose={() => setShowLocationMap(false)}
        latitude={user?.latitude}
        longitude={user?.longitude}
        onSelect={() => setShowLocationMap(false)}
      />
    </div>
  );
}
